import type { ProxVoiceSocialLinks } from "../../lib/services/proxvoice";

export const PUBLIC_CONNECTION_PROVIDER_IDS = [
  "x",
  "substack",
  "telegram",
  "discord",
  "tradingview",
  "financialjuice",
  "edgeful",
  "projectx",
  "rithmic",
  "databento",
  "notion",
  "github",
  "slack",
  "grok",
  "openai",
  "opencode-go",
  "nous",
  "deepseek",
  "anthropic",
  "glm",
] as const;

export type PublicConnectionProviderId =
  (typeof PUBLIC_CONNECTION_PROVIDER_IDS)[number];

export interface PublicConnection {
  id: string;
  providerId: PublicConnectionProviderId;
  label: string;
  handle?: string;
  url: string;
  isPublic: boolean;
}

export interface PublicConnectionProvider {
  id: PublicConnectionProviderId;
  label: string;
  assetPath?: string;
  fieldHint?: string;
}

export const PUBLIC_CONNECTION_PROVIDERS: PublicConnectionProvider[] = [
  { id: "x", label: "X", assetPath: "/brands/x.svg", fieldHint: "@handle" },
  {
    id: "substack",
    label: "Substack",
    assetPath: "/brands/substack.svg",
    fieldHint: "publication.substack.com",
  },
  {
    id: "telegram",
    label: "Telegram",
    assetPath: "/brands/telegram.svg",
    fieldHint: "@handle",
  },
  {
    id: "discord",
    label: "Discord",
    assetPath: "/brands/discord.svg",
    fieldHint: "discord.gg/invite or numeric user ID",
  },
  { id: "tradingview", label: "TradingView" },
  { id: "financialjuice", label: "FinancialJuice" },
  { id: "edgeful", label: "Edgeful", assetPath: "/brands/edgeful.svg" },
  { id: "projectx", label: "ProjectX" },
  { id: "rithmic", label: "Rithmic" },
  { id: "databento", label: "Databento" },
  { id: "notion", label: "Notion" },
  { id: "github", label: "GitHub" },
  { id: "slack", label: "Slack" },
  { id: "grok", label: "Grok" },
  { id: "openai", label: "OpenAI" },
  { id: "opencode-go", label: "OpenCode Go" },
  { id: "nous", label: "Nous Portal" },
  { id: "deepseek", label: "DeepSeek" },
  { id: "anthropic", label: "Anthropic" },
  { id: "glm", label: "GLM" },
];

export const LEGACY_SOCIAL_PROVIDERS = PUBLIC_CONNECTION_PROVIDERS.slice(
  0,
  4,
) as Array<PublicConnectionProvider & { id: keyof ProxVoiceSocialLinks }>;

export function providerForConnection(providerId: PublicConnectionProviderId) {
  return PUBLIC_CONNECTION_PROVIDERS.find(({ id }) => id === providerId);
}

export function normalizePublicConnectionUrl(
  providerId: PublicConnectionProviderId,
  value: string | undefined,
): string | null {
  const raw = value?.trim().replace(/\s+/g, "");
  if (!raw) return null;
  let candidate = raw;
  if (!/^https:\/\//i.test(candidate)) {
    const handle = candidate.replace(/^@+/, "");
    if (providerId === "x") candidate = `https://x.com/${handle}`;
    else if (providerId === "substack") {
      candidate = handle.includes(".")
        ? `https://${handle}`
        : `https://${handle}.substack.com`;
    } else if (providerId === "telegram") {
      candidate = `https://t.me/${handle}`;
    } else if (providerId === "discord" && /^\d{6,24}$/.test(handle)) {
      candidate = `https://discord.com/users/${handle}`;
    } else if (providerId === "discord" && handle.startsWith("discord.gg/")) {
      candidate = `https://${handle}`;
    } else {
      return null;
    }
  }
  try {
    const url = new URL(candidate);
    return url.protocol === "https:" ? url.toString() : null;
  } catch {
    return null;
  }
}

export function connectionFromLegacySocial(
  providerId: keyof ProxVoiceSocialLinks,
  handle: string | undefined,
  isPublic: boolean,
): PublicConnection | null {
  const url = normalizePublicConnectionUrl(providerId, handle);
  const provider = providerForConnection(providerId);
  if (!url || !provider) return null;
  return {
    id: `profile:${providerId}`,
    providerId,
    label: provider.label,
    handle: handle?.trim().replace(/^@+/, ""),
    url,
    isPublic,
  };
}

export function normalizePublicConnections(input: unknown): PublicConnection[] {
  if (!Array.isArray(input)) return [];
  const byProvider = new Map<PublicConnectionProviderId, PublicConnection>();
  input.forEach((entry) => {
    if (!entry || typeof entry !== "object") return;
    const source = entry as Partial<PublicConnection>;
    if (
      !PUBLIC_CONNECTION_PROVIDER_IDS.includes(
        source.providerId as PublicConnectionProviderId,
      )
    )
      return;
    const providerId = source.providerId as PublicConnectionProviderId;
    const url = normalizePublicConnectionUrl(providerId, source.url);
    const provider = providerForConnection(providerId);
    if (!url || !provider) return;
    byProvider.set(providerId, {
      id:
        typeof source.id === "string"
          ? source.id.slice(0, 80)
          : `profile:${providerId}`,
      providerId,
      label:
        typeof source.label === "string"
          ? source.label.slice(0, 48)
          : provider.label,
      handle:
        typeof source.handle === "string"
          ? source.handle.slice(0, 120)
          : undefined,
      url,
      isPublic: source.isPublic === true,
    });
  });
  return [...byProvider.values()];
}

export function publicConnectionsOnly(connections: PublicConnection[]) {
  return connections.filter(({ isPublic }) => isPublic);
}
